import React, { useCallback, useEffect, useRef } from 'react';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import {
  authenticateWithOptions,
  BiometricStrength,
  isSensorAvailable,
} from '@sbaiahmed1/react-native-biometrics';
import { useTheme, GTFonts } from '../../theme';
import { fs, iconSize, v } from '../../utils/libs';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import { selectAllowDeviceCredentialsFallback } from '../../redux/SettingsSlice';
import {
  selectBiometricInFlight,
  selectSensorInfo,
  setBiometricInFlight,
  setSecurityGateStatus,
  setSensorInfo,
} from '../../redux/securityRuntimeSlice';
import { AppAlert } from '../../services/AppAlert';

/**
 * Content component for the security gate modal.
 * Used as overlay when app returns from background and biometrics are required.
 * Renders the unlock UI (logo, fingerprint, tap to unlock) and handles biometric auth.
 */
const SecurityGateContent: React.FC = () => {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const attemptedOnceRef = useRef(false);
  const biometricInFlight = useAppSelector(selectBiometricInFlight);
  const sensor = useAppSelector(selectSensorInfo);
  const allowDeviceCredentialsFallback = useAppSelector(
    selectAllowDeviceCredentialsFallback,
  );

  const ensureSensorAvailable = useCallback(async () => {
    if (sensor?.checked) {
      return {
        available: sensor?.info?.available === true,
        error: sensor?.info?.error,
      };
    }

    try {
      const info = await isSensorAvailable({
        biometricStrength: BiometricStrength.Strong,
      } as any);
      dispatch(setSensorInfo(info as any));
      return {
        available: (info as any)?.available === true,
        error: (info as any)?.error,
      };
    } catch (e: any) {
      dispatch(
        setSensorInfo({
          available: false,
          biometryType: 'Unknown',
          error: e?.message,
          errorCode: e?.code || e?.errorCode,
        } as any),
      );
      return { available: false, error: e?.message };
    }
  }, [dispatch, sensor?.checked, sensor?.info?.available, sensor?.info?.error]);

  const tryUnlock = useCallback(async () => {
    if (biometricInFlight) return;

    const { available, error } = await ensureSensorAvailable();
    if (!available && !allowDeviceCredentialsFallback) {
      AppAlert.alert(
        'Biometrics unavailable',
        error || 'Biometric authentication is not available.',
      );
      return;
    }

    try {
      dispatch(setBiometricInFlight(true));
      const result = await authenticateWithOptions({
        title: 'App locked',
        subtitle: 'Authenticate to continue',
        allowDeviceCredentials: allowDeviceCredentialsFallback,
        disableDeviceFallback: !allowDeviceCredentialsFallback,
        biometricStrength: BiometricStrength.Strong,
      } as any);

      if (result?.success) {
        dispatch(setSecurityGateStatus('unlocked'));
      }
    } catch {
      // Stay on gate modal if auth fails/cancels.
    } finally {
      dispatch(setBiometricInFlight(false));
    }
  }, [
    allowDeviceCredentialsFallback,
    biometricInFlight,
    dispatch,
    ensureSensorAvailable,
  ]);

  useEffect(() => {
    if (attemptedOnceRef.current) return;
    attemptedOnceRef.current = true;
    void tryUnlock();
  }, [tryUnlock]);

  return (
    <Pressable
      style={[styles.container, { backgroundColor: theme.colors.primaryDark }]}
      onPress={tryUnlock}>
      <View style={styles.top}>
        <Image
          source={require('../../assets/notification-icon.png')}
          resizeMode="contain"
          style={styles.logo}
        />
      </View>
      <View style={styles.bottom}>
        <View style={styles.iconWrap}>
          <MaterialCommunityIcons
            name="fingerprint"
            size={iconSize(64)}
            color="rgba(255,255,255,0.9)"
          />
        </View>
        <Text style={styles.title}>Tap to unlock</Text>
        {sensor?.checked && sensor?.info?.available !== true ? (
          <Text style={styles.hint}>
            {allowDeviceCredentialsFallback
              ? 'Use device passcode to continue'
              : 'Biometrics not available'}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
};

export default SecurityGateContent;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  top: {
    justifyContent: 'flex-end',
  },
  bottom: {
    gap: v(16),
    alignItems: 'center',
  },
  logo: {
    marginBottom: v(32),
  },
  iconWrap: {},
  title: {
    fontSize: fs(18),
    lineHeight: fs(22),
    fontFamily: GTFonts.semibold,
    color: 'rgba(255,255,255,0.95)',
    letterSpacing: 0.5,
  },
  hint: {
    marginTop: v(12),
    fontSize: fs(13),
    fontFamily: GTFonts.regular,
    color: 'rgba(255,255,255,0.7)',
  },
});
